import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import type { RewardSummaryStatements } from '@/pages/CompanyReport/redux/selectors/getStatements';
import type { CompetitorProps } from '@/components/Competitors/Competitor';
import { useSelector, shallowEqual } from 'react-redux';
import type { RoutineStatus, UiState } from '@ducks/ui';
import {
  getSummary,
  getRewardSummaryStatements,
  getFirst3Competitors,
  getAnalysisChecks,
} from '@/pages/CompanyReport/redux/selectors';

import {
  getRiskSummaryStatus,
  getFailingRisksData,
} from '@/pages/CompanyReport/redux/selectors/getStatements';
import type {
  AnalysisChecks,
  RiskProps,
  SummaryRiskStatus,
} from '../interface';

interface CompanySummaryState {
  isFund: boolean;
  companyId: string;
  competitors: CompetitorProps[];
  shortDescription: string;
  description: string;
  companyName: string;
  uniqueSymbol: string;
  tickerSymbol: string;
  snowflakePoints: number[];
  fundamentalsSentence?: string;
  analysisChecks: AnalysisChecks;
  rewardsStatements: RewardSummaryStatements;
  hasRewards: boolean;
  summaryRiskStatus: SummaryRiskStatus;
  firstFourFailingRisks: (SelectedStatement & RiskProps)[];
  totalFailingRisks: number;
  fetchStatementsStatus: RoutineStatus;
  allRisks: (SelectedStatement & RiskProps)[];
}

export const useCompanySummaryState = (
  companyId?: string
): CompanySummaryState => {
  const summaryState = useSelector<any, CompanySummaryState>((state) => {
    const ownProps = { companyId };
    const { ui }: { ui: UiState } = state;
    const summary = getSummary(state, ownProps);
    const competitors = getFirst3Competitors(state, ownProps);
    const analysisCheckScores = getAnalysisChecks(state, ownProps);
    const rewardData = getRewardSummaryStatements(state, ownProps);
    const hasRewards = rewardData.hasRewards;
    const summaryRiskStatus = getRiskSummaryStatus(state, ownProps);
    const failingRiskData = getFailingRisksData(state, ownProps);
    const { totalFailingRisks, firstFourFailingRisks, allRisks } =
      failingRiskData;

    return {
      analysisChecks: analysisCheckScores,
      isFund: summary.isFund,
      companyId: companyId as string,
      shortDescription: summary.shortDescription,
      description: summary.description,
      companyName: summary.shortName,
      tickerSymbol: summary.tickerSymbol,
      competitors,
      uniqueSymbol: summary.uniqueSymbol,
      snowflakePoints: summary.snowflakePoints,
      fundamentalsSentence: summary.fundamentals,
      hasRewards,
      summaryRiskStatus,
      firstFourFailingRisks,
      totalFailingRisks,
      allRisks,
      fetchStatementsStatus: ui.fetchStatementsStatus,
      rewardsStatements: rewardData.rewardSummaryStatements,
    };
  }, shallowEqual);

  return summaryState;
};
